import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import MetricDot from "../shared/metricDot";
import {
  areaPath,
  CHART_HEIGHT,
  END_LABEL_OFFSET,
  linePath,
  MAX_DOT_POINTS,
  niceYScale,
  PADDING,
  tickStride,
  xForSlot,
  yForValue,
} from "@repo/components/src/utils/chartGeometry";
import { formatSeparators } from "@repo/components/src/utils/numbers";
import React, { useMemo, useState } from "react";
import { CHART_COLORS } from "../../campaign/analytics/report/shared/chartTokens";
import ChartTooltip from "../shared/chartTooltip";
import { useMeasuredWidth } from "./useMeasuredWidth";

export type EvolutionSeries = {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
};

export type EvolutionChartProps = {
  slots: string[];
  series: EvolutionSeries[];
  showAreaWash?: boolean;
  pointValueLabels?: boolean;
  formatValue?: (n: number) => string;
  formatSlot?: (s: string) => string;
  renderTooltip?: (slotIndex: number) => React.ReactNode;
  height?: number;
};

const AREA_WASH_OPACITY = 0.09;

const EvolutionChart = ({
  slots,
  series,
  showAreaWash = false,
  pointValueLabels = false,
  formatValue = (n: number) => formatSeparators(n),
  formatSlot = (s: string) => s,
  renderTooltip,
  height = CHART_HEIGHT,
}: EvolutionChartProps) => {
  const [wrapperRef, width] = useMeasuredWidth();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const n = slots.length;
  const showDots = n > 0 && n <= MAX_DOT_POINTS;
  const stride = tickStride(n);

  const { max, ticks } = useMemo(() => {
    const allValues = series
      .flatMap((s) => s.values)
      .filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
    const rawMax = allValues.length ? Math.max(...allValues) : 0;
    return niceYScale(rawMax);
  }, [series]);

  if (width === null) {
    return (
      <Box ref={wrapperRef} className="w-full" style={{ height } as object} />
    );
  }

  const innerWidth = Math.max(width - PADDING.left - PADDING.right, 1);
  const innerHeight = Math.max(height - PADDING.top - PADDING.bottom, 1);

  const slotX = (i: number) => PADDING.left + xForSlot(i, n, innerWidth);
  const hitWidth = n > 1 ? innerWidth / (n - 1) : innerWidth;

  const tooltipContent =
    hoverIndex !== null && renderTooltip ? renderTooltip(hoverIndex) : null;
  const tooltipLeft =
    hoverIndex !== null
      ? Math.min(Math.max(slotX(hoverIndex) - 70, 4), width - 148)
      : 0;

  return (
    <Box className="w-full">
      {series.length >= 2 ? (
        <HStack space="md" className="flex-wrap mb-2">
          {series.map((s) => (
            <HStack key={s.key} space="xs" className="items-center">
              <MetricDot color={s.color} />
              <Text size="xs" className="text-typography-600">
                {s.label}
              </Text>
            </HStack>
          ))}
        </HStack>
      ) : null}

      <Box ref={wrapperRef} className="w-full relative">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <g transform={`translate(${PADDING.left},${PADDING.top})`}>
            {ticks.map((tick, i) => {
              const y = yForValue(tick, max, innerHeight);
              return (
                <g key={`grid-${i}`}>
                  <line
                    x1={0}
                    x2={innerWidth}
                    y1={y}
                    y2={y}
                    stroke={CHART_COLORS.track}
                    strokeWidth={1}
                  />
                  <text
                    x={-8}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={11}
                    fill={CHART_COLORS.marker}
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}

            {slots.map((slotLabel, i) =>
              i % stride === 0 ? (
                <text
                  key={`xtick-${i}`}
                  x={xForSlot(i, n, innerWidth)}
                  y={innerHeight + 20}
                  textAnchor="middle"
                  fontSize={11}
                  fill={CHART_COLORS.marker}
                >
                  {formatSlot(slotLabel)}
                </text>
              ) : null,
            )}

            {showAreaWash
              ? series.map((s) => (
                  <path
                    key={`area-${s.key}`}
                    d={areaPath(s.values, innerWidth, innerHeight, max)}
                    fill={s.color}
                    fillOpacity={AREA_WASH_OPACITY}
                    stroke="none"
                  />
                ))
              : null}

            {series.map((s) => (
              <path
                key={`line-${s.key}`}
                d={linePath(s.values, innerWidth, innerHeight, max)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

            {series.map((s) =>
              s.values.map((value, i) => {
                if (value === null || value === undefined) return null;
                const x = xForSlot(i, n, innerWidth);
                const y = yForValue(value, max, innerHeight);
                return (
                  <g key={`point-${s.key}-${i}`}>
                    {showDots ? (
                      <circle cx={x} cy={y} r={3} fill={s.color} />
                    ) : null}
                    {/* Every `stride`-th point only — one label per day was
                        unreadable at 30 days. The last point is deliberately
                        excluded: the end-of-series label below already
                        renders its value, and drawing both doubled it up. */}
                    {pointValueLabels && i % stride === 0 && i !== n - 1 ? (
                      <text
                        x={x}
                        y={y - 8}
                        textAnchor="middle"
                        fontSize={10.5}
                        fill={s.color}
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatValue(value)}
                      </text>
                    ) : null}
                  </g>
                );
              }),
            )}

            {series.map((s) => {
              const lastIndex = (() => {
                for (let i = s.values.length - 1; i >= 0; i--) {
                  const v = s.values[i];
                  if (v !== null && v !== undefined) return i;
                }
                return -1;
              })();
              if (lastIndex < 0) return null;
              const value = s.values[lastIndex]!;
              const x = xForSlot(lastIndex, n, innerWidth);
              const y = yForValue(value, max, innerHeight);
              return (
                <text
                  key={`end-label-${s.key}`}
                  x={x + END_LABEL_OFFSET}
                  y={y}
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={s.color}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {formatValue(value)}
                </text>
              );
            })}

            {hoverIndex !== null ? (
              <line
                x1={xForSlot(hoverIndex, n, innerWidth)}
                x2={xForSlot(hoverIndex, n, innerWidth)}
                y1={0}
                y2={innerHeight}
                stroke={CHART_COLORS.marker}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            ) : null}

            {slots.map((_, i) => (
              <rect
                key={`hit-${i}`}
                x={xForSlot(i, n, innerWidth) - hitWidth / 2}
                y={0}
                width={hitWidth}
                height={innerHeight}
                fill="transparent"
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() =>
                  setHoverIndex((current) => (current === i ? null : current))
                }
              />
            ))}
          </g>
        </svg>

        {tooltipContent ? (
          <ChartTooltip left={tooltipLeft} top={4} content={tooltipContent} />
        ) : null}
      </Box>
    </Box>
  );
};

export default React.memo(EvolutionChart);
